import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { SecuritySettingsSamlSetupModalDomainFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

import Domains from './Domains'
import Metadata from './Metadata'
import { useSamlForm } from './useSamlForm'

interface Props {
  samlName: string
  domains: SecuritySettingsSamlSetupModalDomainFragment[]
  onClose: () => void
  afterSubmit: (success: boolean) => void
}

const SAMLModal = ({ samlName, domains, afterSubmit, onClose }: Props) => {
  const verifiedDomains = domains.filter((d) => d.status === 'verified')

  type Step = 'selectDomains' | 'uploadMetadata'
  const [currentStep, setCurrentStep] = useState<Step>('selectDomains')

  const initialValues = {
    // Let's save the user some time by pre-selecting the domain if there's only one
    domainIds: verifiedDomains.length === 1 ? verifiedDomains.map((d) => d.id) : [],
  }
  const form = useSamlForm(initialValues, afterSubmit)

  const title = () => {
    switch (currentStep) {
      case 'selectDomains':
        return i18n.t('settings.edit_company.saml_sso.modal.domains.title')
      case 'uploadMetadata':
        return i18n.t('settings.edit_company.saml_sso.modal.metadata.title')
    }
  }

  const content = () => {
    switch (currentStep) {
      case 'selectDomains':
        return <Domains form={form} domains={domains} />
      case 'uploadMetadata':
        return <Metadata form={form} samlName={samlName} />
    }
  }

  const stepButtons = () => {
    switch (currentStep) {
      case 'selectDomains':
        return (
          <Button
            disabled={form.values.domainIds.length === 0}
            onClick={() => setCurrentStep('uploadMetadata')}
          >
            {i18n.t('settings.edit_company.saml_sso.modal.buttons.next')}
          </Button>
        )
      case 'uploadMetadata':
        return (
          <>
            <Button onClick={() => setCurrentStep('selectDomains')} type="secondary">
              {i18n.t('settings.edit_company.saml_sso.modal.buttons.back')}
            </Button>
            <Button submit disabled={!form.isValid || form.isSubmitting}>
              {i18n.t('settings.edit_company.saml_sso.modal.buttons.submit')}
            </Button>
          </>
        )
    }
  }

  return (
    <Modal
      illustration="document-edit"
      onClose={onClose}
      onSubmit={() => form.submitForm()}
      title={title()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          {stepButtons()}
        </>
      }
    >
      <Box gap="s24" flexDirection="column" alignItems="center" paddingBottom="s8">
        {content()}
      </Box>
    </Modal>
  )
}

export default SAMLModal
